<template>
  <section class="relative z-10 py-24 px-6 flex flex-col items-center">
    <div class="mb-16 flex flex-col items-center gap-6 text-center">
      <div
        class="inline-flex items-center gap-3 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-md"
      >
        <HelpCircle class="size-4 text-brand" />
        <span class="text-[10px] font-black tracking-[0.3em] text-white/50"
          >Help & Info</span
        >
      </div>

      <h2
        class="text-5xl lg:text-7xl font-black text-white tracking-tighter leading-none"
      >
        Here, <span class="text-brand">Answers</span> to all your
        <span class="text-brand">Questions</span>
      </h2>
      <p class="text-[11px] font-bold text-white/30 tracking-[0.4em]">
        Everything you need to know to get started.
      </p>
    </div>

    <div class="flex w-full flex-col gap-4 lg:max-w-4xl">
      <div
        v-for="(faq, index) in faqs"
        :key="faq.id"
        class="group relative w-full cursor-pointer rounded-[2rem] border border-white/5 bg-white/2 p-8 transition-all duration-500 hover:bg-white/4 hover:border-brand/30"
        :class="{
          'bg-white/5 border-brand/40 shadow-[0_20px_40px_rgba(0,0,0,0.3)]':
            expandedFaqIndex === index,
        }"
        @click="toggleFaqItem(index)"
      >
        <div
          v-if="expandedFaqIndex === index"
          class="absolute inset-0 rounded-[2rem] bg-brand/5 blur-2xl -z-10 animate-pulse"
        />

        <div class="flex items-center justify-between gap-8">
          <h3
            class="text-xl lg:text-2xl font-black tracking-tighter transition-colors"
            :class="expandedFaqIndex === index ? 'text-brand' : 'text-white'"
          >
            <span class="text-white/10 mr-4 font-mono text-sm tracking-widest"
              >0{{ index + 1 }}</span
            >
            {{ faq.title }}
          </h3>

          <div
            class="flex size-10 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 transition-all duration-500"
            :class="{
              'rotate-180 border-brand/50 bg-brand/20':
                expandedFaqIndex === index,
            }"
          >
            <ChevronDown
              class="size-5 transition-colors"
              :class="
                expandedFaqIndex === index ? 'text-brand' : 'text-white/40'
              "
            />
          </div>
        </div>

        <div
          class="grid transition-all duration-500 ease-in-out"
          :class="
            expandedFaqIndex === index
              ? 'grid-rows-[1fr] mt-6 opacity-100'
              : 'grid-rows-[0fr] opacity-0'
          "
        >
          <div class="overflow-hidden">
            <div class="pt-6 border-t border-white/10">
              <p
                class="text-sm lg:text-base font-medium text-white/50 leading-relaxed max-w-2xl"
              >
                {{ faq.description }}
              </p>
              <div class="mt-6 flex gap-2">
                <span
                  class="text-[9px] font-black text-brand/60 tracking-widest px-2 py-1 bg-brand/5 rounded border border-brand/10"
                  >Trusted Info</span
                >
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { ref } from "vue";
import { HelpCircle, ChevronDown } from "lucide-vue-next";

const faqs = [
  {
    id: 1,
    title: "What is Pebble?",
    description:
      "Pebble is your go-to spot for amazing local content. From movies and podcasts to documentaries, it’s all about real stories made by Africans, for Africans and the world.",
  },
  {
    id: 2,
    title: "How do I get started?",
    description:
      "It’s quick and easy. Sign up with your email or phone, log in, pick a plan, and you’re good to go. You’ll have instant access to everything on the platform.",
  },
  {
    id: 3,
    title: "Are there any limits?",
    description:
      "Nope. Once you’re signed up, you can stream as much as you want, anytime you want. No caps, no restrictions—just enjoy.",
  },
  {
    id: 4,
    title: "Is it free?",
    description: "Yes—for now. You can sign up and start watching at no cost.",
  },
  {
    id: 5,
    title: "I’m a creator—how do I join?",
    description:
      "We’re always excited to work with creators. If you’ve got great content to share, reach out to us at content@trypebble.com and let’s talk.",
  },
];

const expandedFaqIndex = ref(0); // Start with first one open for visual impact

function toggleFaqItem(index: number) {
  expandedFaqIndex.value = expandedFaqIndex.value === index ? -1 : index;
}
</script>

<style scoped>
/* Smooth typography scaling */
h2 {
  font-family: "Inter Tight", sans-serif;
}
</style>
